import React from 'react';
import { Box, ButtonBase, Chip, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel, Typography } from '@mui/material';
import { Bot, Company, Snapshot, StatusColor, EXEC_ORDER } from './lib/types';
import { STATUS_LABEL, SortDir, SortKey, execCounts, sortBots, tableBots, verdictChips } from './lib/selectors';
import { fmtIct } from './lib/ict';
import { StatusDot } from './StatusDot';
import { NoReviewChip, UnreadBadge, VerdictChip } from './chips';
import { FOCUS_RING, FONT, OVERLINE, RADIUS, SHADOW, TONE } from './lib/brand';

const clamp3 = { display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical' as const, overflow: 'hidden' };

const COLUMNS: { key: SortKey; label: string; width?: number | string }[] = [
  { key: 'bot', label: 'Bot', width: 190 },
  { key: 'status', label: 'Status', width: 130 },
  { key: 'doing', label: 'Currently doing' },
  { key: 'finding', label: 'Top finding' },
  { key: 'actions', label: 'Actions', width: 190 },
  { key: 'verdict', label: 'C-suite verdict', width: 190 },
  { key: 'updated', label: 'Updated', width: 130 },
];

export function BotTable({
  snapshot, company, status, exec, sort, dir, onSort, onStatus, onExec, onOpenBot, onPreviewBot, onOpenAction, onBlockers, unread = {},
}: {
  snapshot: Snapshot; company: Company; status: StatusColor | null; exec: string | null; sort: SortKey; dir: SortDir;
  onSort: (key: SortKey) => void; onStatus: (c: StatusColor | null) => void; onExec: (e: string | null) => void;
  onOpenBot: (b: Bot) => void; onPreviewBot: (b: Bot) => void; onOpenAction: (id: string) => void; onBlockers: (b: Bot) => void;
  unread?: Record<string, number>;
}) {
  const name = snapshot.companies[company].name;
  const counts = execCounts(snapshot, company);
  const rows = sortBots(tableBots(snapshot, company, { status, exec }), sort, dir);
  const title = exec ? `${name} — bots reporting to / reviewed by ${exec}` : `${name} — all bots`;

  return (
    <Paper variant="outlined" component="section" id="bo-table" aria-label="Bots" data-testid="bot-table" sx={{ borderRadius: RADIUS.xl, boxShadow: SHADOW.card, overflow: 'hidden', mb: 2 }}>
      <Box sx={{ p: 2, pb: 1.2 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
          <Typography component="h2" sx={{ fontFamily: FONT.heading, fontSize: 15, fontWeight: 700 }}>{title}</Typography>
          {status && (
            <Typography variant="caption" color="text.secondary">
              {STATUS_LABEL[status]} only <ButtonBase onClick={() => onStatus(null)} sx={{ color: 'primary.main', textDecoration: 'underline', fontSize: 'inherit' }}>[clear]</ButtonBase>
            </Typography>
          )}
        </Box>
        <Box role="group" aria-label="Filter by C-suite" sx={{ display: 'flex', gap: 0.8, flexWrap: 'wrap', mt: 1.2 }}>
          <Chip clickable size="small" aria-pressed={!exec} color={!exec ? 'primary' : 'default'} variant={!exec ? 'filled' : 'outlined'} label={`All bots ${counts.all}`} onClick={() => onExec(null)} />
          {EXEC_ORDER.map((e) => (
            <Chip
              key={e}
              clickable
              size="small"
              aria-pressed={exec === e}
              color={exec === e ? 'primary' : 'default'}
              variant={exec === e ? 'filled' : 'outlined'}
              label={`${e} ${counts.byExec[e] ?? 0}`}
              onClick={() => onExec(exec === e ? null : e)}
            />
          ))}
        </Box>
      </Box>

      <TableContainer>
        <Table size="small" sx={{ minWidth: 980 }}>
          <TableHead>
            <TableRow>
              {COLUMNS.map((c) => (
                <TableCell key={c.key} sortDirection={sort === c.key ? dir : false} aria-sort={sort === c.key ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'} sx={{ ...OVERLINE, width: c.width, fontSize: 11 }}>
                  <TableSortLabel active={sort === c.key} direction={sort === c.key ? dir : 'asc'} onClick={() => onSort(c.key)}>{c.label}</TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && (
              <TableRow><TableCell colSpan={7}><Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>No bots in this category for this company.</Typography></TableCell></TableRow>
            )}
            {rows.map((b) => {
              const chips = verdictChips(b);
              const top = b.findings && b.findings[0];
              const actions = b.actions || [];
              const hasBlocker = (b.blockers || []).length > 0;
              return (
                <TableRow
                  hover
                  key={b.slug}
                  data-testid={`row-${b.slug}`}
                  onClick={() => onOpenBot(b)}
                  sx={{ cursor: 'pointer', verticalAlign: 'top' }}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                      {/* The bot's name is the keyboard target (a row holding buttons cannot itself be one). */}
                      <ButtonBase
                        onClick={(e) => { e.stopPropagation(); onOpenBot(b); }}
                        aria-label={`${b.name}. Open`}
                        sx={{ fontSize: 13, fontWeight: 700, textAlign: 'left', fontFamily: 'inherit', '&:focus-visible': { outline: FOCUS_RING, outlineOffset: 2 } }}
                      >
                        {b.name}
                      </ButtonBase>
                      <UnreadBadge count={unread[b.slug] || 0} />
                    </Box>
                    <Typography variant="caption" color="text.secondary" component="div">{b.group}</Typography>
                    <ButtonBase onClick={(e) => { e.stopPropagation(); onPreviewBot(b); }} sx={{ fontSize: 11, color: 'primary.main', textDecoration: 'underline' }}>preview</ButtonBase>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7 }}>
                      <StatusDot color={b.color} />
                      <Typography sx={{ fontSize: 12.5, fontWeight: 600 }}>{STATUS_LABEL[b.color]}</Typography>
                    </Box>
                    {hasBlocker && (
                      <ButtonBase onClick={(e) => { e.stopPropagation(); onBlockers(b); }} sx={{ mt: 0.5, fontSize: 10.5, fontWeight: 700, px: 0.8, py: 0.1, borderRadius: RADIUS.sm, bgcolor: TONE.strong.bg, color: TONE.strong.fg }}>blocker</ButtonBase>
                    )}
                  </TableCell>
                  <TableCell><Typography sx={{ fontSize: 12.5, ...clamp3 }}>{(b.doing && b.doing.text) || 'No status file found'}</Typography></TableCell>
                  <TableCell>
                    {top ? (
                      <Box sx={{ ...clamp3 }}>
                        {typeof top.score === 'number' && <Box component="span" sx={{ fontFamily: FONT.mono, fontSize: 11, fontWeight: 700, px: 0.7, mr: 0.7, borderRadius: RADIUS.sm, bgcolor: 'action.selected' }}>{top.score}</Box>}
                        <Typography component="span" sx={{ fontSize: 12.5 }}>{top.title}</Typography>
                      </Box>
                    ) : (
                      <Typography sx={{ fontSize: 12.5, color: 'text.secondary', ...clamp3 }}>
                        {b.latest_output && b.latest_output.title ? `Latest output: ${b.latest_output.title}` : "No scored findings in this bot's files."}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {actions.length === 0 ? (
                      <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>No action items found</Typography>
                    ) : (
                      <ButtonBase
                        onClick={(e) => { e.stopPropagation(); onOpenAction(actions[0].id); }}
                        sx={{ textAlign: 'left', width: '100%', ...clamp3, fontSize: 12.5 }}
                      >
                        <Box component="span" sx={{ fontFamily: FONT.mono, fontSize: 11, fontWeight: 700, px: 0.7, mr: 0.7, borderRadius: RADIUS.sm, bgcolor: 'action.selected' }}>{actions.length}</Box>
                        {actions[0].for || actions[0].text}
                      </ButtonBase>
                    )}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-start' }}>
                      {chips.length ? chips.map((c) => <VerdictChip key={`${c.exec}|${c.verdict}`} exec={c.exec} verdict={c.verdict} />) : <NoReviewChip />}
                    </Box>
                  </TableCell>
                  <TableCell><Typography sx={{ fontSize: 12 }}>{fmtIct(b.last_activity_ict)}</Typography></TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', p: 1.5 }}>
        Showing {rows.length} bots · click a row for full detail with sources · sorted by {COLUMNS.find((c) => c.key === sort)?.label} · times ICT
      </Typography>
    </Paper>
  );
}

